import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs';

import { FiscalDataId, FiscalDataService } from '../../services/fiscal-data.service';
import { CountryService } from '../../services/country.service';
import { StateService } from '../../services/state.service';
import { Globals, SelectOption } from '../../globals';
import { CFDI, CustomerBillData } from '../../services/customer-bill.service';
import { Autocomplete } from '../../coms/autocomplete/autocomplete.component';
import { PostalCodeCatalog } from '../../../catalogs/postalCode';
import { Utils } from '../../util';
import { CompanyData } from '../../services/company.service';

function notRequiredEmailValidator(control: FormControl) {
	if (!control.value) {
			return null;
	}

	return Validators.email(control);
}

@Component({
	selector: 'app-update-fiscal-data',
	templateUrl: './update-fiscal-data.component.html',
	styleUrls: ['./update-fiscal-data.component.css']
})
export class UpdateFiscalDataComponent implements OnInit, OnDestroy {

	id: string;
  parent: string;
  customerOrSupplier: string;

	fiscalDataForm: FormGroup;
	cfdiForm: FormGroup;
  	cfdiSelects: any = {};
	fiscalData$: Observable<any>;

	fiscalDataSub: Subscription;

	countryId: any;
	stateId: any;

	countries: any[] = [];
	@Input() countryInit: string;
	@Output() countryModelChange = new EventEmitter();

	states: any[] = [];
	@Input() stateInit: string;
	@Output() stateModelChange = new EventEmitter();

	localitys: {name: string, value: string}[] = Utils.Object.keys(Globals.LOCALITYS).map(lockKey => ({name: `${lockKey}, ${Globals.LOCALITYS[lockKey].state}`, value: lockKey})).sort((a, b) => a.name > b.name ? 1 : -1);
	locality: string = '';

	municipilatys: {name: string, value: string}[] = Utils.Object.keys(Globals.MUNICIPALITYS).map(munKey => ({name: `${munKey}, ${Globals.MUNICIPALITYS[munKey].state}`, value: munKey})).sort((a,b) => a.name > b.name ? 1 : -1);
	municipality: string = '';

	fiscalRegimenSelect: SelectOption[] = [];
	postalCodeAutocomplete: Autocomplete<string> = new Autocomplete('postalCode', '* Código postal', PostalCodeCatalog.select, postalCode => this.fiscalDataForm.get('postCode').setValue(postalCode || ''));

	constructor(
		private formBuilder: FormBuilder,
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private fiscalDataService: FiscalDataService,
    	private customerBillData: CustomerBillData,
		private countryService: CountryService,
		private stateService: StateService,
		private companyData: CompanyData,
	) {
		this.cfdiSelects = customerBillData.allSelects;
		this.fiscalRegimenSelect = Utils.Object.keys(this.companyData.fiscalRegimenSelect).map(key => ({ value: key, name: this.companyData.fiscalRegimenSelect[key].full }));
		
		this.parent = this.activatedRoute.parent.snapshot.paramMap.get('id');
		this.id = this.activatedRoute.snapshot.paramMap.get('id');
		this.customerOrSupplier = this.activatedRoute.parent.snapshot.data['customerOrSupplier'];
		
		this.fiscalData$ = this.fiscalDataService.getFiscalData(this.id);
		this.fiscalDataSub = this.fiscalData$.subscribe(data => {
			const formData = {
				businessName: data.businessName,
				rfc: data.rfc,
				neighborhood: data.neighborhood,
				street: data.street,
				externalNumber: data.externalNumber,
				internalNumber: data.internalNumber,
				municipality: data.municipality,
				state: data.stateId,
				country: data.countryId,
				email: data.email,
				email2: data.email2,
				phone: data.phone || '',
				creditLimitAmount: data.creditLimitAmount,
				creditLimitDays: data.creditLimitDays,
				creditLimitAmountInside: data.creditLimitAmountInside,
				creditLimitDaysInside: data.creditLimitDaysInside,
				accountingCode: data.accountingCode,
				notes: data.notes,
				postCode: data.postCode,
				fiscalRegimen: data.fiscalRegimen || 'key0',
			};
			this.locality = data.xlocality || '';
			this.municipality = data.xmunicipality || '';
			this.fiscalDataForm.patchValue(formData);
			this.cfdiForm.patchValue(data.cfdi || {});
			this.loadCountriesAndStates();
			this.postalCodeAutocomplete.setByValue(data.postCode);
		});
	}

	ngOnInit() {
		this.fiscalDataForm = this.formBuilder.group({
			businessName: ['', Validators.required],
			rfc: ['', Validators.required],
			neighborhood: ['', Validators.required],
			street: ['', Validators.required],
			externalNumber: ['', Validators.required],
			internalNumber: '',
			municipality: '',
			state: ['', Validators.required],
			country: ['', Validators.required],
			email: ['', notRequiredEmailValidator],
			email2: ['', notRequiredEmailValidator],
			phone: '',
			creditLimitAmount: '',
			creditLimitDays: '',
			creditLimitAmountInside: '',
			creditLimitDaysInside: '',
			accountingCode: '',
			notes: '',
			postCode: ['', Validators.required],
			fiscalRegimen: <FiscalDataId['fiscalRegimen']>'key0',
		});

		this.cfdiForm = this.formBuilder.group(<CFDI>{
		  payForm: 'key0',
		  payMethod: 'key0',
		  payConditions: 'key0',
		  cfdiUsage: 'key0',
		  //currency: 'key0',
		  //change: 1,
		  bankDigits: ''
		});
	}
	ngOnDestroy() {
		this.fiscalDataSub.unsubscribe();
	}

	onSubmitFiscalData({value, validate}: any) {
		value.stateId = value.state;
		value.countryId = value.country;
		delete value.country;
		delete value.state;

		value.cfdi = this.cfdiForm.value;
		value.xlocality = this.locality;
		value.xmunicipality = this.municipality;

		this.fiscalDataService.updateFiscalData(this.id, value);
		if(this.customerOrSupplier == 'customerId'){
			this.router.navigate(['/vw_customer/' + this.parent + '/fiscalData']);
		} else if(this.customerOrSupplier == 'supplierId'){
			this.router.navigate(['/vw_supplier/' + this.parent + '/fiscalData']);
		}
	}
	    	
	getBack() {
		if(this.customerOrSupplier == 'customerId'){
			this.router.navigate(['/vw_customer/' + this.parent + '/fiscalData']);
		} else if(this.customerOrSupplier == 'supplierId'){
			this.router.navigate(['/vw_supplier/' + this.parent + '/fiscalData']);
		}
	}

	loadCountriesAndStates() {
		this.countries = Utils.Object.keys(Globals.COUNTRIES).map(key => ({id$: key, name: Globals.COUNTRIES[key].name}) );
		this.states = Utils.Object.keys(Globals.STATES).map(key => ({id$: key, name: Globals.STATES[key].name}) );
	}
	changeCountry(countryValue) {
		this.fiscalDataForm.controls['country'].setValue(countryValue);
	}
	changeState(stateValue) {
		this.fiscalDataForm.controls['state'].setValue(stateValue);
	}

}